import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ArrowRightLeft, Plus, Lock, ArrowLeft } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { TransferStatusBadge } from '../../components/nursery/despatch/TransferStatusBadge';
import { NewTransferDialog } from '../../components/nursery/despatch/NewTransferDialog';
import type { NurseryTransfer } from '../../types/nursery';

/**
 * /nursery/transfers — trees moving between farms ahead of picking: raised by
 * hand, for a load whose stock is at another farm, or by a despatch area
 * running low. Open ones first, by date.
 */

function TransferList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'TransferList' });
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const [open, setOpen] = useState(true);
  const [transfers, setTransfers] = useState<NurseryTransfer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await nurseryService.getTransfers(open ? { open: true } : undefined);
      if (res.success) setTransfers(res.data || []);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [open, t]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  if (licenseLoading) return <div className="max-w-3xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenceTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">{t('licenceBody')}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}><ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<ArrowRightLeft className="h-6 w-6 text-white" />}
        actionButtons={canManage ? (
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> {t('newTransfer')}</Button>
        ) : undefined}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex gap-2" role="group" aria-label={t('whichTransfers')}>
          {[true, false].map((o) => (
            <button key={String(o)} type="button" aria-pressed={open === o} onClick={() => setOpen(o)}
              className={cn('rounded-lg border px-3 py-1.5 text-sm',
                open === o ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium' : 'bg-white border-neutral-300 text-neutral-600')}>
              {o ? t('openOnes') : t('allTransfers')}
            </button>
          ))}
        </div>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}
        {transfers === null ? (
          !error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : transfers.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
            {open ? t('nothingWaiting') : t('noTransfers')}
          </div>
        ) : (
          <ul className="space-y-3">
            {transfers.map((tr) => (
              <li key={tr.id}>
                <Link to={`/nursery/transfers/${tr.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold text-neutral-900">{tr.transfer_number}</span>
                    <TransferStatusBadge status={tr.status} />
                    {tr.source !== 'manual' && (
                      <span className="text-xs rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5">
                        {t(`source.${tr.source}`)}{tr.load_number ? ` ${tr.load_number}` : ''}
                      </span>
                    )}
                    <span className="ml-auto text-sm font-medium text-neutral-800">{formatLocalDateInTz(tr.scheduled_date, tz)}</span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 break-words">{tr.from_farm_name} → {tr.to_farm_name}, {tr.to_location_name}</p>
                  <p className="text-xs text-neutral-500">
                    {t('summary', { count: tr.line_count, trees: tr.total_trees.toLocaleString() })}
                    {tr.status === 'received' ? ` · ${t('arrived', { n: tr.received_trees?.toLocaleString() })}` : ''}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {creating && (
        <NewTransferDialog onClose={() => setCreating(false)} onCreated={(created) => navigate(`/nursery/transfers/${created.id}`)} />
      )}
    </>
  );
}

export default TransferList;
